import { useEffect } from 'react';
import { pageDataService } from 'common';
import { isITPSupported } from 'common/utilities/FlightSetting';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { CMOnboardingStatus, ITPOnboardingStatus, SSNValidationStatus } from 'mcsclient/models';
import { ApiCallResultStatus } from 'store/apiCallResultStatus';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { getFamilyITPSummaryAsync } from '../identityMonitoringSlice';

export type UseIdentityOnboardingStatusDataType = (csidOverride?: string) => {
  onboardingStatusApiStatus: ApiCallResultStatus;
  itmOnboarded: boolean;
  cmOnboarded: boolean;
  itmOnboardingStatus: ITPOnboardingStatus;
  cmOnboardingStatus: CMOnboardingStatus;
  ssnValidationStatus: SSNValidationStatus;
};

/**
 * Helper hook for getting onboarding status for current user or a family member
 *
 * @param csidOverride specific csid to get onboarding status for
 */
export const useIdentityOnboardingStatus: UseIdentityOnboardingStatusDataType = (csidOverride?: string) => {
  const dispatch = useAppDispatch();
  const csid = useAppSelector((state) => csidOverride || state.identityMonitoring.userContext.csid);
  const isCurrentUser = localeIgnoreCaseEquals(csid, pageDataService.getUserData().csid);

  const [onboardingStatusApiStatus, itmOnboarded, cmOnboarded, itmOnboardingStatus, cmOnboardingStatus, ssnValidationStatus] =
    useAppSelector((state) => {
      const member = state.identityMonitoring.familyMembers.find((m) => localeIgnoreCaseEquals(m.csid, csid));
      return [
        // use current user's onboarding status
        isCurrentUser ? ApiCallResultStatus.SUCCESS : state.identityMonitoring.familyMembersLoadingStatus,
        member?.itpOnboardingStatus === 'Monitoring',
        member?.cmOnboardingStatus === 'Monitoring',
        member?.itpOnboardingStatus || 'Unmonitored',
        member?.cmOnboardingStatus || 'NotAvailable',
        member?.ssnValidationStatus || 'NotAvailable',
      ];
    });

  const shouldLoadOnboardingStatus =
    isITPSupported() && csid && !isCurrentUser && onboardingStatusApiStatus === ApiCallResultStatus.DEFAULT;

  // fetch family member summary for onboarding status
  useEffect(() => {
    if (shouldLoadOnboardingStatus) {
      void dispatch(getFamilyITPSummaryAsync());
    }
  }, [dispatch, csid, shouldLoadOnboardingStatus]);

  return {
    onboardingStatusApiStatus,
    itmOnboarded,
    cmOnboarded,
    itmOnboardingStatus,
    cmOnboardingStatus,
    ssnValidationStatus,
  };
};
